import { Link } from "react-router-dom";
import { PageShell, LegalSection } from "../components/PageShell";

export default function Privacy() {
  return (
    <PageShell eyebrow="Legal" title="Privacy Policy">
      <p className="mt-4 text-sm text-neutral-400">Effective date: June 1, 2026 · Last updated: July 14, 2026</p>
      <p className="mt-6 text-[15px] leading-relaxed text-neutral-600">
        Concentration of Risk ("we", "us", or "our") operates the Concentration of Risk website
        and related services (the "Service"), which provide research tools built on
        concentration-of-risk disclosures extracted from public SEC EDGAR filings. Concentration
        of Risk is the data controller responsible for the personal information described in this
        policy. This Privacy Policy explains what information we collect, how we use it, who we
        share it with, and the choices you have. By using the Service you agree to this policy.
      </p>

      <LegalSection heading="1. Information we collect">
        <p><strong className="text-neutral-800">Account information.</strong> You can create an
        account two ways. If you sign in with Google, we receive and store your name, email
        address, and profile picture from Google; we never receive or store your Google password.
        If you sign in with an email link ("magic link"), we store your email address and send you
        a single-use sign-in link; the link expires after 15 minutes and we never create or store a
        password for you.</p>
        <p><strong className="text-neutral-800">Session data.</strong> We set an essential session
        cookie (<code>session_token</code>) so you stay signed in. Sessions expire automatically
        after 7 days. This cookie is strictly necessary for the Service and is never used for
        advertising.</p>
        <p><strong className="text-neutral-800">Messages you send us.</strong> If you join the
        waitlist, use the contact form, or reply to one of our emails, we store the name, email
        address, and message you provide so we can respond and notify you of updates you
        requested.</p>
        <p><strong className="text-neutral-800">Alert preferences.</strong> If you configure filing
        alerts, we store the tickers, dimensions, and thresholds you choose so we can deliver
        them.</p>
        <p><strong className="text-neutral-800">Usage logs.</strong> Like most web services, our
        servers record basic technical logs (IP address, request path, timestamp) for security and
        reliability. These logs are retained only as long as operationally necessary.</p>
      </LegalSection>

      <LegalSection heading="2. Cookies and analytics">
        <p>We use two categories of cookies and similar technologies:</p>
        <p><strong className="text-neutral-800">Essential.</strong> The <code>session_token</code>
        {" "}cookie described above. The Service cannot keep you signed in without it.</p>
        <p><strong className="text-neutral-800">Analytics.</strong> We use Google Analytics 4 and
        PostHog to understand how the Service is used — pages viewed, features clicked, general
        device and browser type, and approximate location derived from IP address. We use this
        strictly to improve the product; we do not use it to serve advertising, and we do not build
        advertising profiles. Analytics identifiers are pseudonymous; where you are signed in, we
        may associate product events with your account so we can understand and improve your
        experience.</p>
        <p><strong className="text-neutral-800">Specific cookies we set:</strong>{" "}
        <code>session_token</code> — essential; keeps you signed in; expires after 7 days.{" "}
        <code>_ga</code> / <code>_ga_*</code> — Google Analytics; distinguishes visitors; up to 24
        months. <code>ph_*</code> — PostHog; product analytics; up to 12 months.</p>
        <p><strong className="text-neutral-800">Your choices.</strong> You can block or delete
        analytics cookies in your browser settings or with a content blocker without losing access
        to the Service; only the essential session cookie is required to stay signed in. Google
        also offers a{" "}
        <a href="https://tools.google.com/dlpage/gaoptout" target="_blank" rel="noopener noreferrer"
          className="text-[#1a1a18] underline underline-offset-2">browser add-on</a>{" "}
        that opts you out of Google Analytics everywhere.</p>
      </LegalSection>

      <LegalSection heading="3. Emails we send">
        <p>We send three kinds of email: (a) <strong className="text-neutral-800">transactional</strong>
        {" "}— sign-in links and a one-time welcome message; (b)
        {" "}<strong className="text-neutral-800">alerts you configure</strong> — notifications about
        filings matching criteria you set; and (c) occasional
        {" "}<strong className="text-neutral-800">product updates</strong>. You can stop alerts by
        deleting them in the app, and unsubscribe from product updates via the link in any such
        email. We do not send third-party marketing, and we never share your email address for
        others' marketing.</p>
      </LegalSection>

      <LegalSection heading="4. What we do not collect">
        <p>We do not collect government identifiers, precise geolocation, or any special-category
        personal data. If and when paid plans are offered, payments are processed by Stripe; your
        card details go directly to Stripe and never touch our servers — we receive only a payment
        confirmation, the plan purchased, and billing metadata needed for receipts and support.</p>
      </LegalSection>

      <LegalSection heading="5. How we use information (and our legal bases)">
        <p>We use the information above solely to: (a) operate the Service and keep you signed in;
        (b) deliver alerts and emails you asked for; (c) respond to messages you send us;
        (d) understand aggregate usage so we can improve the product; (e) secure the Service and
        prevent abuse; and (f) comply with legal obligations. We do not sell personal information,
        and we do not use it for third-party advertising.</p>
        <p><strong className="text-neutral-800">Legal bases (EEA/UK users).</strong> Where the GDPR
        or UK GDPR applies, we process your data on these bases: <em>performance of a contract</em>
        {" "}(providing the Service, your account, and features you request);
        {" "}<em>legitimate interests</em> (securing the Service, preventing abuse, and analyzing
        aggregate usage to improve the product — balanced against your rights);
        {" "}<em>consent</em> (optional communications, which you can withdraw at any time); and
        {" "}<em>legal obligation</em> (where we must retain or disclose information by law).</p>
      </LegalSection>

      <LegalSection heading="6. Who we share it with">
        <p>We share personal information only with the service providers required to run the
        Service, each acting on our instructions: <strong className="text-neutral-800">Google
        LLC</strong> (sign-in and analytics), <strong className="text-neutral-800">PostHog</strong>
        {" "}(product analytics), <strong className="text-neutral-800">Resend</strong>
        {" "}(transactional email delivery), our cloud hosting and database providers, and — if you
        purchase a paid plan — <strong className="text-neutral-800">Stripe</strong> (payment
        processing). We may also disclose information where required by law, or as part of a
        merger, acquisition, or asset sale (in which case this policy continues to apply to your
        data). We do not sell, rent, or trade your personal information.</p>
      </LegalSection>

      <LegalSection heading="7. Data about public companies">
        <p>The company data displayed on the Service is derived from filings publicly available on
        SEC EDGAR. It concerns corporate issuers, not private individuals, and is not "personal
        information" under this policy.</p>
      </LegalSection>

      <LegalSection heading="8. International transfers">
        <p>The Service is operated from the United States and information is processed on servers
        located there. If you access the Service from outside the U.S., you understand your
        information will be transferred to and processed in the United States, where data
        protection laws may differ from those in your jurisdiction. Where required for EEA/UK
        data, our providers rely on recognized transfer safeguards such as standard contractual
        clauses or Data Privacy Framework certification.</p>
      </LegalSection>

      <LegalSection heading="9. Retention and deletion">
        <p>Account data is retained while your account is active. Session tokens expire after 7
        days; magic-link tokens expire after 15 minutes and are deleted automatically. Analytics
        data is retained per our providers' standard retention settings. You may request deletion
        of your account and associated personal data at any time via the{" "}
        <Link to="/contact" className="text-[#1a1a18] underline underline-offset-2">contact page</Link>;
        we will complete verified requests within 30 days unless we are legally required to retain
        specific records.</p>
      </LegalSection>

      <LegalSection heading="10. Your privacy rights">
        <p><strong className="text-neutral-800">European Economic Area &amp; United Kingdom
        (GDPR / UK GDPR).</strong> You have the right to access, rectify, erase, and receive a
        portable copy of your personal data; to restrict or object to processing (including
        processing based on legitimate interests); and to withdraw consent at any time without
        affecting prior processing. You also have the right to lodge a complaint with your local
        supervisory authority (or the UK Information Commissioner's Office), though we'd appreciate
        the chance to resolve your concern first.</p>
        <p><strong className="text-neutral-800">California (CCPA/CPRA).</strong> You have the right
        to know the categories and specific pieces of personal information we collect (they are
        listed in Section 1: identifiers such as name and email, internet activity, and inferences
        limited to product usage), to delete it, to correct it, and to not be discriminated against
        for exercising your rights. We do not "sell" or "share" personal information as defined by
        the CCPA/CPRA, we do not use or disclose sensitive personal information, and we have not
        done so in the preceding 12 months — accordingly, no opt-out is required.</p>
        <p><strong className="text-neutral-800">Other U.S. states.</strong> If you reside in a
        state with a comprehensive privacy law (including Virginia, Colorado, Connecticut, Utah,
        Texas, and others), you have comparable rights of access, correction, deletion, and
        portability, and the right to appeal a refusal — reply to our response and we will
        re-review it, or you may contact your state Attorney General.</p>
        <p><strong className="text-neutral-800">Exercising rights.</strong> Use the{" "}
        <Link to="/contact" className="text-[#1a1a18] underline underline-offset-2">contact page</Link>{" "}
        from the email associated with your account (or provide information sufficient for us to
        verify you). Authorized agents may submit requests with proof of authorization. We respond
        within the timeframe the applicable law requires (typically 30–45 days) and never
        discriminate against you for exercising a right.</p>
        <p><strong className="text-neutral-800">Do Not Track and Global Privacy Control.</strong>
        {" "}Because we do not sell or share personal information or run cross-site advertising,
        there is nothing for these signals to opt you out of; our essential cookie is required for
        sign-in regardless. We disclose, per California law, that the Service does not otherwise
        respond to browser "Do Not Track" signals. You can block analytics cookies at any time as
        described in Section 2.</p>
      </LegalSection>

      <LegalSection heading="11. Automated decision-making">
        <p>We do not use your personal data to make automated decisions that produce legal or
        similarly significant effects about you. Analytics are used only in aggregate to improve
        the product.</p>
      </LegalSection>

      <LegalSection heading="12. Security">
        <p>We use industry-standard safeguards: HTTPS everywhere, httpOnly secure session cookies,
        single-use expiring sign-in links, and access controls on our infrastructure. No method of
        transmission or storage is 100% secure, but we work to protect your information
        proportionate to its sensitivity. If we learn of a breach affecting your personal data, we
        will notify you as required by applicable law.</p>
      </LegalSection>

      <LegalSection heading="13. Children">
        <p>The Service is not directed to children under 16, and we do not knowingly collect
        personal information from them. If you believe a child has provided us information, contact
        us and we will delete it.</p>
      </LegalSection>

      <LegalSection heading="14. Changes to this policy">
        <p>We may update this policy from time to time. Material changes will be reflected by a new
        "last updated" date at the top of this page, and, where appropriate, additional notice on
        the Service or by email. Continued use after changes take effect constitutes acceptance.</p>
      </LegalSection>

      <LegalSection heading="15. Contact">
        <p>Questions about privacy, or want to exercise a data right? Reach us through the{" "}
        <Link to="/contact" className="text-[#1a1a18] underline underline-offset-2">contact page</Link>.</p>
      </LegalSection>
    </PageShell>
  );
}
